/**
 * SignManager - Spawns and manages road signs with dynamic canvas textures
 *
 * Signs appear on the right side of the road displaying information to the player.
 * Supports multiple sign types via registry pattern - any system can register
 * custom sign types with their own models, styling, and content generators.
 *
 * Usage:
 *   signManager.registerSignType(WAVE_SIGN_TYPE);
 *   signManager.setDefaultSignType('wave');
 *   signManager.spawnSign(worldZ, 'wave', { customData });
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { LIGHTING } from '../config/constants.js';

export class SignManager {
    constructor(scene, camera, biomeManager, worldTranslator, gameState, config = {}, preloader = null) {
        this.scene = scene;
        this.camera = camera;
        this.biomeManager = biomeManager;
        this.worldTranslator = worldTranslator;
        this.gameState = gameState;

        // Self-register for preload tracking with dispose callback
        this._onPreloadComplete = preloader?.register('signs', {
            dispose: () => this.dispose()
        });

        // LightingManager reference (set via setLightingManager)
        this.lightingManager = null;

        // GLTF loader instance
        this.loader = new GLTFLoader();

        // ═══════════════════════════════════════════════════════════════════════
        // SIGN TYPE REGISTRY
        // ═══════════════════════════════════════════════════════════════════════

        // Registered sign types: { typeId: signTypeConfig }
        this.signTypes = {};

        // Loaded models cache: { modelPath: gltfScene }
        this.signModels = {};

        // Model loading promises: { modelPath: Promise }
        this.modelLoadingPromises = {};

        // Default sign type for auto-spawning
        this.defaultSignType = null;

        // Original texture image (shared across all signs)
        this.originalTextureImage = null;
        this.originalTextureLoaded = false;

        // ═══════════════════════════════════════════════════════════════════════
        // CONFIGURATION - Spawn and positioning
        // ═══════════════════════════════════════════════════════════════════════

        // Spawn interval in game units (try 500-3000)
        this.spawnIntervalUnits = config.spawnIntervalUnits || 3000;

        // Distance ahead of player to spawn signs (so player doesn't see them pop in)
        this.spawnDistance = config.spawnDistance || 1500;

        // Lateral offset from road edge in units (try 0.5-3)
        this.signOffsetFromRoad = config.signOffsetFromRoad || 10;

        // Vertical offset from road surface (try -1 to 2)
        this.signYOffset = config.signYOffset || -12;

        // Model scale (try 0.1-0.3)
        this.signScale = config.signScale || 1;

        // ═══════════════════════════════════════════════════════════════════════
        // SIGN LIGHTS - Point lights on the sign fixtures
        // ═══════════════════════════════════════════════════════════════════════

        // Enable/disable sign fixture lights
        this.signLightsEnabled = config.signLightsEnabled !== false;

        // Light brightness
        this.signLightIntensity = config.signLightIntensity || 2;

        // Light distance (how far the light reaches)
        this.signLightDistance = config.signLightDistance || 30;

        // Light color
        this.signLightColor = config.signLightColor || 0xffffee;

        // Left light position (x, y, z)
        this.signLightLeftPos = config.signLightLeftPos || [-12.5, 24, 1.3];

        // Right light position (x, y, z)
        this.signLightRightPos = config.signLightRightPos || [-4.7, 24, 1.3];

        // Debug: show spheres at light positions
        this.signLightDebug = config.signLightDebug || false;

        // Night threshold - uses same value as headlights from constants
        this.nightThreshold = config.nightThreshold || LIGHTING.HEADLIGHTS.NIGHT_THRESHOLD;

        // Current sign lights visibility state
        this.signLightsVisible = false;

        // ═══════════════════════════════════════════════════════════════════════
        // DEFAULT TEXT FORMATTING - Used when sign type doesn't specify
        // ═══════════════════════════════════════════════════════════════════════

        this.defaultFontSizes = {
            single: config.fontSizeSingle || 30,
            line1: config.fontSizeLine1 || 26,
            line2: config.fontSizeLine2 || 20
        };
        this.defaultLineSpacing = config.lineSpacing || 14;
        this.defaultFontFamily = config.fontFamily || 'Arial, sans-serif';
        this.defaultTextColor = config.textColor || '#FFFFFF';
        this.defaultBackgroundColor = config.signBackgroundColor || '#00AA00';
        this.defaultBorderColor = config.borderColor || '#FFFFFF';
        this.defaultEmissiveIntensity = config.emissiveIntensity || 1.0;

        // Maximum signs in pool (try 3-10)
        this.maxVisibleSigns = config.maxVisibleSigns || 5;

        // Distance behind camera to cull signs (try 50-200)
        this.cullDistance = config.cullDistance || 100;

        // ═══════════════════════════════════════════════════════════════════════
        // STATE
        // ═══════════════════════════════════════════════════════════════════════

        // Object pool of sign instances
        this.signPool = [];

        // Active signs in the world
        this.activeSigns = [];

        // Track last spawn position (world Z)
        this.lastSpawnWorldZ = 0;

        // Game units per mile (matching WavePickupManager)
        this.gameUnitsPerMile = 450;

        // Initialization state
        this.initialized = false;

        // Disposal state
        this._disposed = false;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // SIGN TYPE REGISTRATION
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Register a sign type configuration
     * @param {object} signType - Sign type config with id, getContent, styling, etc.
     */
    registerSignType(signType) {
        if (!signType.id) {
            console.error('SignManager: Sign type must have an id');
            return;
        }
        if (!signType.getContent || typeof signType.getContent !== 'function') {
            console.error(`SignManager: Sign type '${signType.id}' must have a getContent function`);
            return;
        }

        this.signTypes[signType.id] = signType;
        console.log(`SignManager: Registered sign type '${signType.id}'`);

        // Set as default if it's the first one registered
        if (!this.defaultSignType) {
            this.defaultSignType = signType.id;
        }
    }

    /**
     * Set the default sign type for auto-spawning
     * @param {string} typeId - Sign type ID
     */
    setDefaultSignType(typeId) {
        if (!this.signTypes[typeId]) {
            console.warn(`SignManager: Unknown sign type '${typeId}'`);
            return;
        }
        this.defaultSignType = typeId;
    }

    /**
     * Get a registered sign type by ID
     * @param {string} typeId - Sign type ID
     * @returns {object|null} - Sign type config or null
     */
    getSignType(typeId) {
        return this.signTypes[typeId] || null;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // INITIALIZATION
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Initialize the manager - load base texture
     */
    async initialize() {
        // Load the original texture image (shared by all signs)
        await this.loadOriginalTexture();

        // Initialize spawn position ahead of current world position
        const worldOffset = this.worldTranslator ? this.worldTranslator.getGroupOffset() : 0;
        const currentWorldZ = -worldOffset;

        // Set initial spawn position
        const firstSignZ = currentWorldZ - this.spawnDistance;
        this.lastSpawnWorldZ = firstSignZ;

        this.initialized = true;

        // Spawn first sign if we have a default type
        if (this.defaultSignType) {
            await this.spawnSign(firstSignZ, this.defaultSignType);
        }

        this._onPreloadComplete?.();
    }

    /**
     * Load the original texture image (shared by all sign types)
     */
    async loadOriginalTexture() {
        return new Promise((resolve, reject) => {
            this.originalTextureImage = new Image();
            this.originalTextureImage.onload = () => {
                this.originalTextureLoaded = true;
                console.log('SignManager: Loaded original texture');
                resolve();
            };
            this.originalTextureImage.onerror = reject;
            this.originalTextureImage.src = './assets/models/baked/overhead_colored_test.jpg';
        });
    }

    /**
     * Load a model by path (cached)
     * @param {string} modelPath - Path to GLTF model
     * @returns {Promise<THREE.Object3D>} - Loaded model scene
     */
    async loadModel(modelPath) {
        // Return cached model if available
        if (this.signModels[modelPath]) {
            return this.signModels[modelPath];
        }

        // Return existing promise if already loading
        if (this.modelLoadingPromises[modelPath]) {
            return this.modelLoadingPromises[modelPath];
        }

        // Start loading
        this.modelLoadingPromises[modelPath] = new Promise((resolve, reject) => {
            this.loader.load(
                modelPath,
                (gltf) => {
                    this.signModels[modelPath] = gltf.scene;
                    console.log(`SignManager: Loaded model '${modelPath}'`);
                    resolve(gltf.scene);
                },
                undefined,
                (error) => {
                    console.error(`SignManager: Failed to load model '${modelPath}':`, error);
                    reject(error);
                }
            );
        });

        return this.modelLoadingPromises[modelPath];
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // TEXTURE CREATION
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Create a canvas texture for a specific sign with given content and styling
     * @param {object} signType - Sign type configuration
     * @param {object} content - { single } or { line1, line2 }
     * @returns {object} - { canvasTexture, emissiveTexture }
     */
    createSignTexture(signType, content) {
        // Get styling from sign type with fallbacks to defaults
        const fontSizes = signType.fontSizes || this.defaultFontSizes;
        const lineSpacing = signType.lineSpacing ?? this.defaultLineSpacing;
        const fontFamily = signType.fontFamily || this.defaultFontFamily;
        const textColor = signType.textColor || this.defaultTextColor;
        const borderColor = signType.borderColor || this.defaultBorderColor;
        const emissiveIntensity = signType.emissiveIntensity ?? this.defaultEmissiveIntensity;

        // Create main canvas
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 256;
        const ctx = canvas.getContext('2d');

        // Create emissive canvas
        const emissiveCanvas = document.createElement('canvas');
        emissiveCanvas.width = 256;
        emissiveCanvas.height = 256;
        const emCtx = emissiveCanvas.getContext('2d');

        const width = canvas.width;
        const height = canvas.height;

        // Clear main canvas
        ctx.clearRect(0, 0, width, height);

        // Clear emissive canvas to black
        emCtx.fillStyle = '#000000';
        emCtx.fillRect(0, 0, width, height);

        // Draw white outline around the bottom 127 pixels (front of sign)
        const borderWidth = signType.borderWidth ?? 6;
        const frontTop = height - 127;
        emCtx.strokeStyle = borderColor;
        emCtx.lineWidth = borderWidth;
        emCtx.strokeRect(
            borderWidth / 2,
            frontTop + borderWidth / 2,
            width - borderWidth,
            127 - borderWidth
        );

        // Draw original texture if loaded (for the gray frame)
        if (this.originalTextureLoaded && this.originalTextureImage) {
            ctx.drawImage(this.originalTextureImage, 0, 0, width, height);
        } else {
            ctx.fillStyle = '#808080';
            ctx.fillRect(0, 0, width, height);
        }

        // Text area
        const greenBoxHeight = 127;
        const greenBoxCenterY = frontTop + greenBoxHeight / 2;
        const padding = 16; // Horizontal padding from edges

        // Draw text
        ctx.fillStyle = textColor;
        emCtx.fillStyle = '#ffffff';

        // New format: rows with label (left) and distance (right)
        if (content.rows && content.rows.length > 0) {
            const rowCount = content.rows.length;
            const rowHeight = Math.min(28, greenBoxHeight / (rowCount + 0.5));
            const startY = greenBoxCenterY - ((rowCount - 1) * rowHeight) / 2;

            content.rows.forEach((row, index) => {
                const y = startY + index * rowHeight;
                const fontSize = row.fontSize || fontSizes.line1 || 22;
                ctx.font = `bold ${fontSize}px ${fontFamily}`;
                emCtx.font = `bold ${fontSize}px ${fontFamily}`;

                // Draw label (left-aligned)
                if (row.label) {
                    ctx.textAlign = 'left';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(row.label, padding, y);

                    emCtx.textAlign = 'left';
                    emCtx.textBaseline = 'middle';
                    emCtx.fillText(row.label, padding, y);
                }

                // Draw distance (right-aligned)
                if (row.distance !== undefined) {
                    const distText = `${row.distance}mi`;
                    ctx.textAlign = 'right';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(distText, width - padding, y);

                    emCtx.textAlign = 'right';
                    emCtx.textBaseline = 'middle';
                    emCtx.fillText(distText, width - padding, y);
                }
            });
        } else if (content.single) {
            // Single centered line (legacy format)
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.font = `bold ${fontSizes.single}px ${fontFamily}`;
            ctx.fillText(content.single, width / 2, greenBoxCenterY);

            emCtx.textAlign = 'center';
            emCtx.textBaseline = 'middle';
            emCtx.font = `bold ${fontSizes.single}px ${fontFamily}`;
            emCtx.fillText(content.single, width / 2, greenBoxCenterY);
        } else if (content.line1) {
            // Two centered lines (legacy format)
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            emCtx.textAlign = 'center';
            emCtx.textBaseline = 'middle';

            ctx.font = `bold ${fontSizes.line1}px ${fontFamily}`;
            ctx.fillText(content.line1, width / 2, greenBoxCenterY - lineSpacing);

            emCtx.font = `bold ${fontSizes.line1}px ${fontFamily}`;
            emCtx.fillText(content.line1, width / 2, greenBoxCenterY - lineSpacing);

            if (content.line2) {
                ctx.font = `bold ${fontSizes.line2}px ${fontFamily}`;
                ctx.fillText(content.line2, width / 2, greenBoxCenterY + lineSpacing);

                emCtx.font = `bold ${fontSizes.line2}px ${fontFamily}`;
                emCtx.fillText(content.line2, width / 2, greenBoxCenterY + lineSpacing);
            }
        }

        // Create Three.js textures
        const canvasTexture = new THREE.CanvasTexture(canvas);
        canvasTexture.minFilter = THREE.LinearFilter;
        canvasTexture.magFilter = THREE.LinearFilter;
        canvasTexture.flipY = false;

        const emissiveTexture = new THREE.CanvasTexture(emissiveCanvas);
        emissiveTexture.minFilter = THREE.LinearFilter;
        emissiveTexture.magFilter = THREE.LinearFilter;
        emissiveTexture.flipY = false;

        return { canvasTexture, emissiveTexture, emissiveIntensity };
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // SIGN INSTANCE CREATION
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Create a sign instance for a specific type with given textures
     * @param {object} signType - Sign type configuration
     * @param {THREE.Object3D} modelTemplate - Model to clone
     * @param {object} textures - { canvasTexture, emissiveTexture, emissiveIntensity }
     * @returns {THREE.Object3D} - Sign instance
     */
    createSignInstance(signType, modelTemplate, textures) {
        const sign = modelTemplate.clone();

        // Apply scale (type-specific or default)
        const scale = signType.scale ?? this.signScale;
        sign.scale.set(scale, scale, scale);

        // Get mesh name to texture
        const signMeshName = signType.signMeshName || 'sign';

        // Find the sign face mesh and apply textures
        let lightsMesh = null;
        sign.traverse((child) => {
            if (child.isMesh) {
                if (child.name === signMeshName) {
                    child.material = new THREE.MeshStandardMaterial({
                        map: textures.canvasTexture,
                        side: THREE.DoubleSide,
                        roughness: 0.5,
                        metalness: 0.0,
                        emissive: 0xffffff,
                        emissiveMap: textures.emissiveTexture,
                        emissiveIntensity: textures.emissiveIntensity
                    });
                } else if (child.name === 'lights') {
                    lightsMesh = child;
                    if (child.material) {
                        child.material = child.material.clone();
                    }
                } else {
                    if (child.material) {
                        child.material = child.material.clone();
                    }
                }
            }
        });

        // Add point lights for illumination
        if (this.signLightsEnabled && lightsMesh) {
            const leftLight = new THREE.PointLight(
                this.signLightColor,
                this.signLightIntensity,
                this.signLightDistance,
                1
            );
            leftLight.position.set(...this.signLightLeftPos);
            sign.add(leftLight);

            const rightLight = new THREE.PointLight(
                this.signLightColor,
                this.signLightIntensity,
                this.signLightDistance,
                1
            );
            rightLight.position.set(...this.signLightRightPos);
            sign.add(rightLight);

            sign.userData.signLights = [leftLight, rightLight];

            leftLight.visible = this.signLightsVisible;
            rightLight.visible = this.signLightsVisible;

            if (this.signLightDebug) {
                const sphereGeom = new THREE.SphereGeometry(0.5, 8, 8);
                const posMat = new THREE.MeshBasicMaterial({ color: 0xffff00 });

                const leftPosSphere = new THREE.Mesh(sphereGeom, posMat);
                leftPosSphere.position.set(...this.signLightLeftPos);
                sign.add(leftPosSphere);

                const rightPosSphere = new THREE.Mesh(sphereGeom, posMat);
                rightPosSphere.position.set(...this.signLightRightPos);
                sign.add(rightPosSphere);
            }
        }

        // Store textures for disposal
        sign.userData.textures = textures;
        sign.userData.signType = signType.id;

        // Add to world
        this.worldTranslator.add(sign);

        return sign;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // SPAWNING
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Spawn a sign at a specific world Z position
     * @param {number} worldZ - The world Z coordinate to spawn at
     * @param {string} typeId - Sign type ID (defaults to defaultSignType)
     * @param {object} customData - Custom data passed to getContent
     */
    async spawnSign(worldZ, typeId = this.defaultSignType, customData = {}) {
        if (!typeId) {
            console.warn('SignManager: No sign type specified and no default set');
            return null;
        }

        const signType = this.signTypes[typeId];
        if (!signType) {
            console.warn(`SignManager: Unknown sign type '${typeId}'`);
            return null;
        }

        const biome = this.biomeManager?.currentBiome;
        if (!biome) return null;

        const roadData = biome.getRoadDataAtZ(worldZ);
        if (!roadData) return null;

        // Get model path (or use default)
        const modelPath = signType.modelPath || './assets/models/road_sign_v1.gltf';

        // Ensure model is loaded
        let modelTemplate;
        try {
            modelTemplate = await this.loadModel(modelPath);
        } catch (error) {
            console.error(`SignManager: Could not load model for sign type '${typeId}'`);
            return null;
        }

        // Calculate miles at sign position
        const worldOffset = this.worldTranslator ? this.worldTranslator.getGroupOffset() : 0;
        const playerWorldZ = -worldOffset;
        const distanceToSign = playerWorldZ - worldZ;
        const currentMiles = this.gameState?.getTotalMiles() || 0;
        const milesAtSign = currentMiles + (distanceToSign / this.gameUnitsPerMile);

        // Get content from sign type
        const context = {
            gameState: this.gameState,
            worldZ,
            milesAtSign,
            customData
        };
        const content = signType.getContent(context);

        // Create texture for this sign
        const textures = this.createSignTexture(signType, content);

        // Create sign instance
        const sign = this.createSignInstance(signType, modelTemplate, textures);

        // Calculate position on right side of road
        const roadWidth = biome.roadWidth || 10;
        const position = roadData.position.clone();
        position.addScaledVector(roadData.right, roadWidth / 2 + this.signOffsetFromRoad);

        // Apply type-specific Y offset or default
        const yOffset = signType.yOffset ?? this.signYOffset;
        sign.position.copy(position);
        sign.position.y += yOffset;

        // Rotate to face oncoming traffic
        const angle = Math.atan2(roadData.tangent.x, roadData.tangent.z);
        sign.rotation.y = angle + Math.PI;

        // Store metadata
        sign.visible = true;
        sign.userData.worldZ = worldZ;
        sign.userData.milesAtSign = milesAtSign;

        // Add to active list
        this.activeSigns.push(sign);

        return sign;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // LIFECYCLE
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Return a sign to be disposed
     * @param {THREE.Object3D} sign
     */
    releaseSign(sign) {
        sign.visible = false;

        // Dispose textures
        const textures = sign.userData.textures;
        if (textures) {
            textures.canvasTexture?.dispose();
            textures.emissiveTexture?.dispose();
        }

        // Dispose lights
        const lights = sign.userData.signLights;
        if (lights) {
            for (const light of lights) {
                sign.remove(light);
                light.dispose?.();
            }
        }

        // Dispose meshes and materials
        sign.traverse((child) => {
            if (child.isMesh) {
                child.geometry?.dispose();
                child.material?.dispose();
            }
        });

        // Remove from world
        this.worldTranslator.remove(sign);

        // Remove from active list
        const index = this.activeSigns.indexOf(sign);
        if (index !== -1) {
            this.activeSigns.splice(index, 1);
        }
    }

    /**
     * Set the lighting manager reference for night detection
     * @param {LightingManager} lightingManager
     */
    setLightingManager(lightingManager) {
        this.lightingManager = lightingManager;
    }

    /**
     * Update sign lights visibility based on ambient light level
     */
    updateSignLightsVisibility() {
        if (!this.signLightsEnabled) return;

        const ambientIntensity = this.lightingManager?.getAmbientIntensity() ?? 1;
        const shouldBeVisible = ambientIntensity < this.nightThreshold;

        if (shouldBeVisible !== this.signLightsVisible) {
            this.signLightsVisible = shouldBeVisible;

            for (const sign of this.activeSigns) {
                const lights = sign.userData.signLights;
                if (lights) {
                    for (const light of lights) {
                        light.visible = shouldBeVisible;
                    }
                }
            }
        }
    }

    /**
     * Cull signs that have passed behind the camera
     */
    cullSigns() {
        const worldOffset = this.worldTranslator ? this.worldTranslator.getGroupOffset() : 0;

        for (let i = this.activeSigns.length - 1; i >= 0; i--) {
            const sign = this.activeSigns[i];
            const visualZ = sign.position.z + worldOffset;

            if (visualZ > this.cullDistance) {
                this.releaseSign(sign);
            }
        }
    }

    /**
     * Main update loop - call every frame
     * @param {number} deltaTime - Time since last frame in seconds
     */
    update(deltaTime) {
        if (this._disposed || !this.initialized || !this.defaultSignType) return;

        const worldOffset = this.worldTranslator ? this.worldTranslator.getGroupOffset() : 0;
        const currentWorldZ = -worldOffset;
        const currentMiles = this.gameState?.getTotalMiles() || 0;

        // Get the sign type configuration
        const signType = this.signTypes[this.defaultSignType];

        // Check if we should spawn at the next interval position
        const spawnZ = currentWorldZ - this.spawnDistance;
        const nextSpawnZ = this.lastSpawnWorldZ - this.spawnIntervalUnits;

        if (spawnZ < nextSpawnZ) {
            // Calculate what mile this sign would be at
            const distanceToSign = currentWorldZ - nextSpawnZ;
            const milesAtSign = currentMiles + (distanceToSign / this.gameUnitsPerMile);

            // Check if sign type says there's content worth displaying
            // If no shouldSpawnSign method, always spawn (backward compatibility)
            const shouldSpawn = !signType?.shouldSpawnSign ||
                signType.shouldSpawnSign(this.gameState, milesAtSign);

            if (shouldSpawn) {
                this.spawnSign(nextSpawnZ, this.defaultSignType);
            }

            // Always update lastSpawnWorldZ so we don't re-check this position
            this.lastSpawnWorldZ = nextSpawnZ;
        }

        // Update sign lights visibility
        this.updateSignLightsVisibility();

        // Cull signs behind camera
        this.cullSigns();
    }

    /**
     * Dispose of all resources
     */
    dispose() {
        if (this._disposed) return;
        this._disposed = true;

        // Release all active signs
        for (const sign of [...this.activeSigns]) {
            this.releaseSign(sign);
        }

        this.activeSigns = [];
        this.signPool = [];

        // Clear model cache
        this.signModels = {};
        this.modelLoadingPromises = {};

        // Clear original texture
        this.originalTextureImage = null;
        this.originalTextureLoaded = false;

        // Clear sign types
        this.signTypes = {};
        this.defaultSignType = null;

        // Clear lighting manager
        this.lightingManager = null;

        this.initialized = false;
    }
}
